import { MAPBOX_TOKEN } from '~config'

const SEARCH_BOX_URL = 'https://api.mapbox.com/search/searchbox/v1'

// "brand" suggestions (e.g. the Walmart chain itself) are excluded because they
// resolve to a list of stores rather than a single location.
const SEARCH_TYPES = 'poi,address,street,place,locality,neighborhood,postcode,district,region'

export interface MapboxAddressComponents {
  street: string
  unit: string
  city: string
  state: string
  zip: string
}

export interface MapboxPlaceSuggestion {
  /** Identifier passed to the retrieve endpoint to get coordinates. */
  mapbox_id: string
  /** Label shown in the suggestion list, e.g. "Walmart, 400 Park Pl, Secaucus, NJ". */
  place_name: string
  /** Name of a business or landmark, empty for plain addresses. */
  poi_name: string
}

export interface MapboxPlace {
  center: [number, number]
  address: MapboxAddressComponents
  place_name: string
}

interface ISuggestion {
  mapbox_id: string
  name: string
  feature_type: string
  address?: string
  full_address?: string
  place_formatted?: string
}

interface IRetrieveContextEntry {
  name?: string
  region_code?: string
}

interface IRetrieveFeature {
  geometry: { coordinates: [number, number] }
  properties: {
    name?: string
    address?: string
    full_address?: string
    place_formatted?: string
    feature_type?: string
    context?: {
      address?: IRetrieveContextEntry
      street?: IRetrieveContextEntry
      place?: IRetrieveContextEntry
      locality?: IRetrieveContextEntry
      region?: IRetrieveContextEntry
      postcode?: IRetrieveContextEntry
    }
  }
}

/**
 * Session tokens group the suggest calls that lead to a retrieve call into a
 * single billable search session, and must be regenerated after each retrieve.
 */
export const createMapboxSessionToken = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

/**
 * Suggests addresses as well as businesses and landmarks matching the query,
 * ordered by distance from `proximity` when provided.
 */
export const suggestMapboxPlaces = async (
  query: string,
  {
    sessionToken,
    proximity,
    limit = 10,
  }: {
    sessionToken: string
    proximity?: { lat: number; lon: number } | null
    limit?: number
  },
): Promise<MapboxPlaceSuggestion[]> => {
  const params = new URLSearchParams({
    q: query,
    access_token: MAPBOX_TOKEN ?? '',
    session_token: sessionToken,
    country: 'us',
    types: SEARCH_TYPES,
    limit: String(limit),
  })
  if (proximity) {
    params.set('proximity', `${proximity.lon},${proximity.lat}`)
  }

  const response = await fetch(`${SEARCH_BOX_URL}/suggest?${params.toString()}`)
  if (!response.ok) {
    throw new Error('Search failed')
  }

  const data: { suggestions?: ISuggestion[] } = await response.json()
  return (data.suggestions ?? []).map((suggestion) => {
    const isPoi = suggestion.feature_type === 'poi'
    const details = suggestion.full_address ?? suggestion.place_formatted ?? ''
    return {
      mapbox_id: suggestion.mapbox_id,
      place_name: isPoi && details ? `${suggestion.name}, ${details}` : details || suggestion.name,
      poi_name: isPoi ? suggestion.name : '',
    }
  })
}

/** Resolves a suggestion into coordinates and address components. */
export const retrieveMapboxPlace = async (
  mapboxId: string,
  { sessionToken }: { sessionToken: string },
): Promise<MapboxPlace | null> => {
  const params = new URLSearchParams({
    access_token: MAPBOX_TOKEN ?? '',
    session_token: sessionToken,
  })

  const response = await fetch(
    `${SEARCH_BOX_URL}/retrieve/${encodeURIComponent(mapboxId)}?${params.toString()}`,
  )
  if (!response.ok) {
    throw new Error('Retrieving location failed')
  }

  const data: { features?: IRetrieveFeature[] } = await response.json()
  const feature = data.features?.[0]
  if (!feature) return null

  const { properties } = feature
  const context = properties.context ?? {}
  return {
    center: feature.geometry.coordinates,
    address: {
      street:
        properties.address ??
        context.address?.name ??
        context.street?.name ??
        properties.name ??
        '',
      unit: '',
      city: context.place?.name ?? context.locality?.name ?? '',
      state: context.region?.region_code ?? '',
      zip: context.postcode?.name ?? '',
    },
    place_name:
      properties.full_address ??
      [properties.name, properties.place_formatted].filter(Boolean).join(', '),
  }
}
